import { UserModel } from "common/models/userModel";

export interface SignInResponseData {
  tokenIsValid: boolean;
  jwtToken: JwtToken;
  user: UserModel;
}

export interface JwtToken {
  accessToken: string;
  refreshToken: string;
}

export interface UserResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  organisation_name: string;
  category: string;
  slug?: string;
  country: string;
  state: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_phone_number: string;
  is_organisation_registered: boolean;
  registration_number: string;
  proof_of_registration: string;
  user_type: string;
  bio: string;
  phone_number: string;
  make_campaigns_private: boolean;
  keep_profile_private: boolean;
  donate_anonymously: boolean;
  opt_in_news_updates: boolean;
  opt_in_thank_you_emails: boolean;
  opt_in_campaign_milestone_updates: boolean;
  facebook?: string;
  tiktok?: string;
  linkedin?: string;
  instagram?: string;
  x?: string;
  profile_picture?: string;
  banner_picture?: string;
}

export interface ProfileResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  organisation_name: string;
  category: string;
  country: string;
  state: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_phone_number: string;
  is_organisation_registered: boolean;
  registration_number: string;
  proof_of_registration: string;
  user_type: string;
  bio: string | null;
  profile_picture: string | null;
  banner_picture: string | null;
  phone_number: string | null;
  slug?: string;
  facebook: string | null;
  tiktok: string | null;
  linkedin: string | null;
  instagram: string | null;
  x: string | null;
  opt_in_news_updates: boolean;
  opt_in_thank_you_emails: boolean;
  opt_in_campaign_milestone_updates: boolean;
  make_campaigns_private: boolean;
  keep_profile_private: boolean;
  donate_anonymously: boolean;
}

export function userModelFromProfile(profile: ProfileResponse): UserModel {
  return {
    id: profile.id,
    firstName: profile.first_name,
    lastName: profile.last_name,
    email: profile.email,
    organisationName: profile.organisation_name,
    category: profile.category,
    country: profile.country,
    state: profile.state,
    adminFirstName: profile.admin_first_name,
    adminLastName: profile.admin_last_name,
    adminPhoneNumber: profile.admin_phone_number,
    isOrganisationRegistered: profile.is_organisation_registered,
    registrationNumber: profile.registration_number,
    proofOfRegistration: profile.proof_of_registration,
    userType: profile.user_type,
    bio: profile.bio,
    profilePicture: profile.profile_picture,
    bannerPicture: profile.banner_picture,
    phoneNumber: profile.phone_number,
    slug: profile.slug,
    socials: {
      facebook: profile.facebook,
      tiktok: profile.tiktok,
      linkedin: profile.linkedin,
      instagram: profile.instagram,
      x: profile.x,
    },
    notificationSettings: {
      optInNewsUpdates: profile.opt_in_news_updates,
      optInThankYouEmails: profile.opt_in_thank_you_emails,
      optInCampaignMilestoneUpdates: profile.opt_in_campaign_milestone_updates,
    },
    privacySettings: {
      makeCampaignsPrivate: profile.make_campaigns_private,
      keepProfilePrivate: profile.keep_profile_private,
      donateAnonymously: profile.donate_anonymously,
    },
  };
}

export function userModelFromObject(user: UserResponse): UserModel {
  return {
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    organisationName: user.organisation_name,
    category: user.category,
    country: user.country,
    state: user.state,
    slug: user.slug,
    adminFirstName: user.admin_first_name,
    adminLastName: user.admin_last_name,
    adminPhoneNumber: user.admin_phone_number,
    isOrganisationRegistered: user.is_organisation_registered,
    registrationNumber: user.registration_number,
    proofOfRegistration: user.proof_of_registration,
    userType: user.user_type,
    bio: user.bio ?? null,
    profilePicture: user.profile_picture ?? null,
    bannerPicture: user.banner_picture ?? null,
    phoneNumber: user.phone_number ?? null,
    socials: {
      facebook: user.facebook ?? null,
      tiktok: user.tiktok ?? null,
      linkedin: user.linkedin ?? null,
      instagram: user.instagram ?? null,
      x: user.x ?? null,
    },
    notificationSettings: {
      optInNewsUpdates: user.opt_in_news_updates,
      optInThankYouEmails: user.opt_in_thank_you_emails,
      optInCampaignMilestoneUpdates: user.opt_in_campaign_milestone_updates,
    },
    privacySettings: {
      makeCampaignsPrivate: user.make_campaigns_private,
      keepProfilePrivate: user.keep_profile_private,
      donateAnonymously: user.donate_anonymously,
    },
  };
}
